import { useMemo, useState } from "react";
import { Copy, Check, MailCheck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import type { components } from "@dolfin/api-admin";

type ClientInviteWithTokenResponse = components["schemas"]["ClientInviteWithTokenResponse"];

interface InviteRevealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invite: ClientInviteWithTokenResponse | null;
}

export function InviteRevealDialog({ open, onOpenChange, invite }: InviteRevealDialogProps) {
  const [copied, setCopied] = useState(false);

  const link = useMemo(() => {
    if (!invite) return "";
    return `${window.location.origin}/accept-invite?token=${encodeURIComponent(invite.token)}`;
  }, [invite]);

  async function copy() {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Invite sent</DialogTitle>
          <DialogDescription>
            An invite email is on its way to {invite?.email}. They'll be asked to sign in with Google
            using that same address.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm">
          <MailCheck className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <span>
            Email delivered to <strong>{invite?.email}</strong>. They can click the link from their
            inbox — no further action needed from you.
          </span>
        </div>

        <div className="relative">
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 border-t border-dashed border-border" />
          <div className="relative mx-auto inline-block bg-popover px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            or share the link directly
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          If they didn't get the email (spam folder, typo, prefer Slack…), copy the accept link and
          send it however you like. The link works just like the one in the email.
        </p>

        <div className="flex items-center gap-2">
          <code className="flex-1 break-all rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-xs">
            {link}
          </code>
          <Button variant="outline" size="sm" onClick={copy} disabled={!link}>
            {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            {copied ? "Copied" : "Copy link"}
          </Button>
        </div>

        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <span>
            The link is shown only once — close this dialog and we can't get it back. To re-issue, revoke
            from the Pending invites list and create a new invite.
          </span>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
